// ===========================================================================
// X-axis category labels that fit their slot
//
// Both Moonlake charts put one text label under each bar group, centred in a
// slot of `groupWidth` px. A long name ("UnitedHealthcare / Plan 1", "Cosentyx
// to Best Infliximab") is wider than the slot and ran into its neighbours. A
// label now wraps at spaces onto at most MAX_LINES lines inside the slot; what
// still does not fit ends in "…". The full name stays on hover (<title>).
// ===========================================================================

const MAX_LINES = 2;
/** Space kept clear between neighbouring labels. */
const SLOT_PADDING = 8;
const ELLIPSIS = "…";

/**
 * Write `label` into `text` (an SVG <text> already placed at the slot's centre,
 * anchored middle, with its font set) as tspans that fit `groupWidth`.
 * The first line sits where a single-line label did (dy 1em).
 */
export function fitAxisLabel(text: SVGTextElement, label: string, groupWidth: number): void {
  const maxWidth = Math.max(groupWidth - SLOT_PADDING, 12);
  const x = text.getAttribute("x") || "0";
  const ns = text.namespaceURI;
  text.removeAttribute("dy");
  text.textContent = "";

  const title = document.createElementNS(ns, "title");
  title.textContent = label;
  text.appendChild(title);

  const probe = document.createElementNS(ns, "tspan") as SVGTSpanElement;
  text.appendChild(probe);
  const widthOf = (s: string): number => {
    probe.textContent = s;
    return probe.getComputedTextLength();
  };

  // Not laid out (a hidden card): measuring returns 0, so draw it whole.
  const lines = widthOf(label) > 0 ? wrap(label, maxWidth, widthOf) : [label];
  text.removeChild(probe);

  lines.forEach((line, i) => {
    const span = document.createElementNS(ns, "tspan");
    span.setAttribute("x", x);
    span.setAttribute("dy", i === 0 ? "1em" : "1.2em");
    span.textContent = line;
    text.appendChild(span);
  });
}

/** Greedy word wrap to MAX_LINES; the last line, or a lone over-long word, is shortened with "…". */
function wrap(label: string, maxWidth: number, widthOf: (s: string) => number): string[] {
  const words = label.split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let line = "";
  for (let i = 0; i < words.length; i++) {
    const next = line ? `${line} ${words[i]}` : words[i];
    if (!line || widthOf(next) <= maxWidth) {
      line = next;
      continue;
    }
    lines.push(line);
    if (lines.length === MAX_LINES - 1) {
      line = words.slice(i).join(" ");
      break;
    }
    line = words[i];
  }
  lines.push(line);
  return lines.map((l) => shorten(l, maxWidth, widthOf));
}

function shorten(line: string, maxWidth: number, widthOf: (s: string) => number): string {
  if (widthOf(line) <= maxWidth) return line;
  let s = line;
  while (s.length > 1 && widthOf(s + ELLIPSIS) > maxWidth) s = s.slice(0, -1);
  return s.trimEnd() + ELLIPSIS;
}
